<template>
    <div class="row q-col-gutter-md">
        <div
            class="col-sm-6 col-md-4 col-xs-12"
            v-for="(card, index) in cards"
            :key="index"
        >
            <q-card
                @click="handleNavigate(card.route)"
                class="q-pa-md bg-accent card"
                :id="card.id"
            >
                <q-card-section class="text-center">
                    <q-icon :name="card.icon" color="white" size="xl"></q-icon>
                </q-card-section>
                <q-card-section>
                    <div
                        class="text-weight-bold text-center card__label ellipsis"
                    >
                        {{ card.label }}
                    </div>
                </q-card-section>
            </q-card>
        </div>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { openURL } from 'quasar';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useSettingsStore } from '@src/stores/settings';
import { mapState } from 'pinia';

export default {
    name: 'Settings',
    mixins: [BreadcrumbsManager],
    created() {
        //  Cards to show in setting page based on user's role.
        this.cards = this.cards.filter(card => this.isShowCard(card.route));

        // Clear selected facility to display the correct breadcrumbs
        // when the page settings is shared between global and facility settings
        const facilitiesStore = useFacilitiesStore();
        facilitiesStore.clearSelectedFacilityAction();
    },
    mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Global Settings')
            }
        ]);
    },
    data() {
        return {
            cards: [
                {
                    label: this.$t('User Management'),
                    icon: 'fas fa-users',
                    route: {
                        name: 'user_list'
                    },
                    id: 'user-management-card'
                },
                {
                    label: this.$t('Site Administration'),
                    icon: 'fas fa-tools',
                    route: {
                        name: 'admin_portal'
                    },
                    id: 'site-administration-card'
                },
                {
                    label: this.$t('System Status'),
                    icon: 'fas fa-heartbeat',
                    route: {
                        name: 'system_status'
                    },
                    id: 'system-status-card'
                },
                {
                    label: this.$t('Device Management'),
                    icon: 'fa-solid fa-router',
                    route: {
                        name: 'device_management'
                    },
                    id: 'device-management'
                },
                {
                    label: this.$t('Product Categories'),
                    icon: 'fa-solid fa-tag',
                    route: {
                        name: 'product_categories'
                    },
                    id: 'product-categories'
                }
            ]
        };
    },
    computed: {
        ...mapState(useSettingsStore, ['isLiveOEEModuleEnabled']),
        /**
         * Determines the URL for the admin portal based on environment configuration.
         * @returns {string} Complete URL to the admin portal login page
         */
        adminPortalURL() {
            const adminPath = '/admin/login/?next=/admin/';
            // If the tenantURL is set in the environment variables, use it.
            if (process.env.TENANT_URL) {
                return process.env.TENANT_URL + adminPath;
            } else {
                // Get the tenantURL, on mobile, the tenantURL is stored in localStorage on login.
                const tenantURL = localStorage.getItem('tenantURL');
                // If tenantURL is not set, use the relative url from the current host.
                if (!tenantURL) {
                    return (
                        window.location.protocol +
                        '//' +
                        window.location.host +
                        adminPath
                    );
                }
                // remove /v1.1/api/ from the end of the tenantURL and append the adminPath.
                return (
                    tenantURL.substring(0, tenantURL.length - 10) + adminPath
                );
            }
        }
    },
    methods: {
        /**
         * Determines if a settings card should be displayed based on user permissions.
         * Admin portal is only visible to admin users, all other cards are visible to everyone.
         * @param {Object} route - Route configuration object
         * @returns {boolean} True if the card should be displayed
         */
        isShowCard(route) {
            if (route.name === 'admin_portal') {
                return this.$isUserAdmin();
            }

            if (route.name === 'device_management') {
                return !this.$isUserPlanner() && this.isLiveOEEModuleEnabled;
            }

            if (route.name === 'product_categories') {
                return !this.$isUserPlanner() && this.isLiveOEEModuleEnabled;
            }

            return true;
        },
        /**
         * Handle route navigation.
         * @param {Object} route - Route configuration object
         */
        handleNavigate(route) {
            if (route.name === 'admin_portal') {
                // Open new tab for desktop and open in browser for mobile.
                // Using Quasar's openURL function to open the link in the browser for mobile devices compatibility.
                // https://quasar.dev/quasar-utils/other-utils#Open-External-URL
                openURL(
                    this.adminPortalURL,
                    // Callback function if the URL does not open
                    undefined,
                    // Options for the window.open() function
                    {
                        // Block the new window or tab to access the window.opener property of the parent window.
                        noopener: true,
                        // Do not send the Referer header when the new window or tab is opened.
                        noreferrer: true,
                        menubar: this.$q.platform.is.mobile,
                        toolbar: this.$q.platform.is.mobile
                    }
                );
            } else {
                this.$router.push({ name: route.name });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.card {
    cursor: pointer;
}

.card__label {
    color: white;
}
</style>
